/**
 * Video generation request detection
 * Detects if user wants to generate a video based on keywords and patterns
 */

const VIDEO_KEYWORDS = [
  'video', 'clip', 'footage', 'animation', 'movie', 'film',
  'cinematic', 'scene', 'sequence', 'motion', 'moving',
  'animate', 'cinematic'
];

const VIDEO_PHRASES = [
  'create a video',
  'generate a video',
  'make a video',
  'create video',
  'generate video',
  'make video',
  'video of',
  'clip of',
  'footage of',
  'animate',
  'show me a video',
  'make an animation',
  'create an animation',
  'generate animation',
  'film',
  'cinematic scene'
];

/**
 * Detects if a message is requesting video generation
 * @param {string} message - User's message
 * @returns {boolean} - True if video generation is likely requested
 */
export function isVideoGenerationRequest(message) {
  if (!message || typeof message !== 'string') return false;

  const lowerMessage = message.toLowerCase().trim();

  // Check for exact phrase matches (high confidence)
  for (const phrase of VIDEO_PHRASES) {
    if (lowerMessage.includes(phrase)) {
      return true;
    }
  }

  // Check for keyword combinations (medium confidence)
  // Must have at least 2 keywords or 1 keyword + descriptive action
  const keywordMatches = VIDEO_KEYWORDS.filter(keyword =>
    lowerMessage.includes(keyword)
  );

  if (keywordMatches.length >= 2) {
    return true;
  }

  // If contains "video" + action verb, likely video request
  if (lowerMessage.includes('video') &&
      (lowerMessage.includes('create') ||
       lowerMessage.includes('generate') ||
       lowerMessage.includes('make') ||
       lowerMessage.includes('show'))) {
    return true;
  }

  return false;
}

/**
 * Extracts the actual video prompt from user's message
 * Removes command words like "create video", "generate", etc.
 * @param {string} message - User's message
 * @returns {string} - Cleaned prompt for video generation
 */
export function extractVideoPrompt(message) {
  let prompt = message.trim();

  // Remove common command prefixes
  const prefixes = [
    /^(please\s+)?(create|generate|make)\s+(a\s+)?(video|clip|animation|footage)\s+(of\s+)?/i,
    /^(please\s+)?show\s+me\s+(a\s+)?(video|clip|animation)\s+(of\s+)?/i,
    /^(can\s+you\s+)?(please\s+)?(create|generate|make)\s+(a\s+)?video\s+/i,
    /^animate\s+/i,
    /^film\s+/i
  ];

  for (const prefix of prefixes) {
    prompt = prompt.replace(prefix, '');
  }

  return prompt.trim();
}
